import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { FormDiscardChangesBlocker } from 'app/agriculture/forms/components/FormDiscardChangesBlocker/FormDiscardChangesBlocker';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import { FormProvider, useForm } from 'react-hook-form';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { useContext } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { useNavigate } from 'react-router-dom';
import { useUserDetailsUpdateMutation } from 'app/agriculture/mutations/user/useUserDetailsUpdateMutation';
import { useCurrentUserDetails } from 'app/agriculture/hooks/users/useCurrentUserDetails';
import './UserProfile.scss';
import { ProfileAvatar } from 'app/agriculture/infobox/common/ProfileAvatar';
import { StatDisplay } from 'app/agriculture/components/StatDisplay/StatDisplay';
import { format as formatDate, formatDistanceToNow } from 'date-fns';
import { getTimestampDateFormat } from 'app/agriculture/components/Timestamp/timestampUtils';
import { Title } from 'app/agriculture/components/Title/Title';
import { Subtitle } from 'app/agriculture/components/Subtitle/Subtitle';
import { BackOfficeContent } from 'app/agriculture/routes/BackOffice/BackOfficeContent';

interface EditProfileFormFields {
    name: string;
    phone: string | null;
}

export function UserProfile() {
    const { email, name, phone, id, lastLogin, createdAt } = useCurrentUserDetails();
    const navigate = useNavigate();
    const form = useForm<EditProfileFormFields>({
        mode: 'all',
        defaultValues: {
            name: name,
            phone: phone,
        },
        shouldFocusError: true,
    });
    const { mutate, isPending } = useUserDetailsUpdateMutation();
    const { agricultureToastService } = useContext(ServicesContext);

    return (
        <FormProvider {...form}>
            <form
                onSubmit={form.handleSubmit((fields) => {
                    mutate(
                        {
                            id,
                            ...fields,
                        },
                        {
                            onSuccess: () => {
                                agricultureToastService
                                    .showToast({
                                        title: 'User profile updated successfully',
                                        type: AgricultureToastType.SUCCESS,
                                        variant: 'simple',
                                    })
                                    .catch((e: unknown) => {
                                        console.error(e);
                                    });
                                form.reset(fields);
                            },
                        },
                    );
                })}
            >
                <BackOfficeContent className="UserProfile">
                    <div className="UserProfile__heading">
                        <Title>User Profile</Title>
                        <Subtitle>Update your details and keep your profile up to date.</Subtitle>
                    </div>

                    <FormDiscardChangesBlocker />
                    <div className="UserProfile__card">
                        <div className="UserProfile__avatar_container">
                            <div className="UserProfile__badge">
                                <ProfileAvatar name={name ?? 'Unknown'} size={168} />
                            </div>
                            <div className="UserProfile__stats">
                                {createdAt != null ? (
                                    <StatDisplay
                                        value={formatDistanceToNow(createdAt, { addSuffix: true })}
                                        name="Date added"
                                        tooltip={formatDate(
                                            createdAt,
                                            getTimestampDateFormat(createdAt),
                                        )}
                                    />
                                ) : null}
                                {lastLogin != null ? (
                                    <StatDisplay
                                        value={formatDistanceToNow(lastLogin, { addSuffix: true })}
                                        name="Last Login"
                                        tooltip={formatDate(
                                            lastLogin,
                                            getTimestampDateFormat(lastLogin),
                                        )}
                                    />
                                ) : null}
                            </div>
                        </div>
                        <div className="UserProfile__form_container">
                            <Input
                                label="Name"
                                {...form.register('name', {
                                    maxLength: {
                                        value: 150,
                                        message: 'Name must be less than 150 characters',
                                    },
                                    required: 'Please enter a name',
                                })}
                                errorMessage={form.formState.errors.name?.message}
                            />
                            <Input label="Email" value={email} disabled />
                            <Input
                                label="Phone Number"
                                {...form.register('phone', {
                                    required: 'Please enter a phone number',
                                    pattern: {
                                        // This matches phone numbers that optionally start with '+' and contain only digits, spaces, hyphens, and parentheses.
                                        // It allows various formats like "+1 (123) 456-7890" or "123-456-7890" but doesn't validate specific phone number structures or lengths.
                                        value: /^\+?[0-9\s-()]+$/,
                                        message: 'Please enter a valid phone number',
                                    },
                                })}
                                errorMessage={form.formState.errors.phone?.message}
                            />
                        </div>
                    </div>
                    <Actions
                        sticky
                        primaryAction={{
                            text: 'Save Changes',
                            type: 'submit',
                            isLoading: isPending,
                            disabled: !form.formState.isDirty,
                        }}
                        backAction={{
                            text: 'Back',
                            onClick: () => {
                                navigate(-1);
                            },
                        }}
                    />
                </BackOfficeContent>
            </form>
        </FormProvider>
    );
}
